import { useEffect } from 'react';

/**
 * JsonLd — injects a JSON-LD structured data <script> into <head> via useEffect.
 *
 * WHY NOT <Helmet><script>:
 * react-helmet-async renders <script type="application/ld+json"> nodes inside
 * <div id="root"> during react-snap prerendering (React 19 portaling changes).
 * At hydration time React's virtual DOM does not include those script nodes
 * (Helmet portals them), causing React Error #418 (hydration mismatch).
 *
 * This component renders null in the React tree (no DOM nodes from React's
 * perspective) and injects the script imperatively after each mount.
 * react-snap executes useEffect in its headless Puppeteer run, so the script
 * still appears in <head> of the prerendered HTML for SEO crawlers.
 */
function JsonLd({ data }) {
  useEffect(() => {
    const json = JSON.stringify(data);

    // react-snap executes useEffect during headless crawl and the resulting
    // script ends up in <head> of the serialised HTML. When the real browser
    // loads that HTML the script is already present — reuse it so we don't
    // create a duplicate, but still register cleanup so SPA navigation removes it.
    const existing = [...document.head.querySelectorAll('script[type="application/ld+json"]')]
      .find(s => s.textContent === json);

    if (existing) {
      return () => existing.remove();
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = json;
    document.head.appendChild(script);
    return () => script.remove();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

export default JsonLd;
